/**
 * The header search dropdown's read: a capped, both-domain probe for the
 * debounced query, as a thin TanStack query (`queryKeys` → fetcher →
 * hook, the house shape). Disabled below the minimum length, so a short
 * query issues nothing and holds no stale data.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import {
  fetchGlobalSearch,
  GLOBAL_SEARCH_MIN_LENGTH,
  type GlobalSearchResults,
} from 'api/globalSearchApi';
import { logger } from 'utils/logger';

import { globalSearchKeys } from './queryKeys';

/** Whether `query` is long enough to search — the hook's own `enabled`. */
export const isDropdownQuery = (query: string): boolean =>
  query.length >= GLOBAL_SEARCH_MIN_LENGTH;

export function useGlobalSearchDropdownQuery(
  query: string,
): UseQueryResult<GlobalSearchResults, Error> {
  return useQuery({
    queryKey: globalSearchKeys.dropdown(query),
    queryFn: async ({ signal }): Promise<GlobalSearchResults> => {
      try {
        return await fetchGlobalSearch(query, signal);
      } catch (error: unknown) {
        if (!signal?.aborted) {
          // Narrowed: a raw axios error would drag the request/response
          // pair (bearer header included) into a log that fires in production.
          logger.warn('[globalSearch] dropdown search failed', {
            message: error instanceof Error ? error.message : String(error),
          });
        }
        throw error;
      }
    },
    enabled: isDropdownQuery(query),
    // A typeahead probe: the next keystroke supersedes it, so a failed
    // attempt is not retried — which also means the warning above fires
    // once per real failure, never for an attempt a retry would recover.
    retry: false,
  });
}
